import {
  EMPLOYEE_PACK_MEMBER_MAX,
  type EmployeePackMember,
} from "#app/utils/marketplace/schemas";

export type EnrichedEmployeePackMember = EmployeePackMember & {
  resolutionFailed: boolean;
};

/** Matches workflow service `POST .../onboard-operator` `installType`. */
export type MarketplaceOnboardInstallType =
  | "single"
  | "normal_pack"
  | "orchestrator_pack";

export type MarketplaceOnboardMemberWorkflow = {
  workflowId: string;
  versionPublicId: string;
};

/**
 * Resolve target workflow id from `subworkflow_call` whether it is a string id or an object
 * (e.g. `{ workflow_id, input_mapping_agent_prompt, ... }`).
 */
function normalizeSubworkflowTargetRef(call: unknown): string | undefined {
  if (call === null || call === undefined) return undefined;
  if (typeof call === "string") {
    const id = call.trim();
    return id || undefined;
  }
  if (typeof call === "object" && !Array.isArray(call)) {
    const o = call as Record<string, unknown>;
    const nested =
      o.workflow_id ??
      o.workflowId ??
      o.subworkflow_id ??
      o.subworkflowId ??
      o.workflow_public_id ??
      o.workflowPublicId;
    if (typeof nested === "string") {
      const id = nested.trim();
      return id || undefined;
    }
  }
  return undefined;
}

function parseMaybeJsonObject(value: unknown): Record<string, unknown> | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function hasStepsShape(o: Record<string, unknown>): boolean {
  const steps = o.steps;
  return Array.isArray(steps) || (steps !== null && typeof steps === "object");
}

/**
 * Find the object that holds `steps` (array or map). Workflow APIs often nest the
 * graph under `workflow.definition`, stringified JSON, or `data.workflow`.
 */
export function resolveWorkflowGraphRoot(
  workflowPayload: unknown,
): Record<string, unknown> | null {
  const raw = workflowPayload as Record<string, unknown> | null | undefined;
  if (!raw || typeof raw !== "object") return null;

  const unwrapDefinition = (
    o: Record<string, unknown> | null,
  ): Record<string, unknown> | null => {
    if (!o) return null;
    if (hasStepsShape(o)) return o;
    const def = parseMaybeJsonObject(o.definition);
    if (def) {
      if (hasStepsShape(def)) return def;
      const nested = unwrapDefinition(def);
      if (nested) return nested;
    }
    const opGraph = parseMaybeJsonObject(o.operator_graph);
    if (opGraph) {
      if (hasStepsShape(opGraph)) return opGraph;
      const nested = unwrapDefinition(opGraph);
      if (nested) return nested;
    }
    return null;
  };

  const candidates: unknown[] = [];
  if (raw.workflow !== undefined) candidates.push(raw.workflow);
  if (raw.data !== undefined && typeof raw.data === "object") {
    const d = raw.data as Record<string, unknown>;
    if (d.workflow !== undefined) candidates.push(d.workflow);
    candidates.push(raw.data);
  }
  candidates.push(raw);

  for (const c of candidates) {
    const o = parseMaybeJsonObject(c);
    const hit = o ? unwrapDefinition(o) : null;
    if (hit) return hit;
  }

  return null;
}

/**
 * Collect non-null subworkflow references from steps: top-level `subworkflow_call`
 * (string or object with workflow_id) and common `config.*` shapes.
 */
export function extractSubworkflowRefsFromDefinition(
  workflowPayload: unknown,
): {
  workflowPublicId: string;
  referencingStepIds: string[];
}[] {
  const workflow = resolveWorkflowGraphRoot(workflowPayload);
  if (!workflow) return [];

  const steps = workflow.steps;
  const byId = new Map<string, string[]>();

  const addRef = (stepId: string, call: unknown) => {
    const id = normalizeSubworkflowTargetRef(call);
    if (!id) return;
    const arr = byId.get(id) ?? [];
    if (!arr.includes(stepId)) arr.push(stepId);
    byId.set(id, arr);
  };

  const collectFromStep = (stepId: string, record: Record<string, unknown>) => {
    addRef(stepId, record.subworkflow_call);
    const cfg = record.config;
    if (cfg && typeof cfg === "object") {
      const c = cfg as Record<string, unknown>;
      addRef(stepId, c.subworkflow_call);
      addRef(
        stepId,
        c.subworkflow_id ?? c.subworkflowId ?? c.workflow_id ?? c.workflowId,
      );
    }
  };

  if (Array.isArray(steps)) {
    for (const step of steps) {
      if (!step || typeof step !== "object") continue;
      const s = step as Record<string, unknown>;
      const stepId = String(s.id ?? s.step_id ?? "").trim() || "unknown";
      collectFromStep(stepId, s);
    }
  } else if (steps && typeof steps === "object") {
    for (const [stepId, meta] of Object.entries(
      steps as Record<string, unknown>,
    )) {
      if (!meta || typeof meta !== "object") continue;
      collectFromStep(stepId, meta as Record<string, unknown>);
    }
  }

  const orderedIds = [...byId.keys()];
  return orderedIds.map((workflowPublicId) => ({
    workflowPublicId,
    referencingStepIds: byId.get(workflowPublicId) ?? [],
  }));
}

function countEmployeePackMembersWithIds(members: unknown): number {
  if (!Array.isArray(members)) return 0;
  let n = 0;
  for (const m of members) {
    if (!m || typeof m !== "object") continue;
    const id = (m as { workflowPublicId?: unknown }).workflowPublicId;
    if (typeof id === "string" && id.trim()) n += 1;
  }
  return n;
}

/**
 * True when pack metadata indicates the root workflow delegates to child workflows via
 * `subworkflow_call` (persisted members from graph extraction, or step ids on a member).
 */
function membersIndicateOrchestratorWorkflow(members: unknown): boolean {
  if (!Array.isArray(members)) return false;
  for (const m of members) {
    if (!m || typeof m !== "object") continue;
    const row = m as { source?: string; referencingStepIds?: unknown };
    if (row.source === "extracted") return true;
    if (
      Array.isArray(row.referencingStepIds) &&
      row.referencingStepIds.length > 0
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Derives onboard `installType` from listing flags, persisted pack members, and the
 * published workflow snapshot (subworkflow calls in the graph).
 *
 * - **orchestrator_pack**: root workflow references other workflows via `subworkflow_call` /
 *   config subworkflow ids (see `extractSubworkflowRefsFromDefinition`), or persisted
 *   members were extracted from that graph.
 * - **normal_pack**: bundled operators (employee pack / multiple member workflows) without
 *   subworkflow nesting in the root graph.
 * - **single**: one standalone workflow.
 */
/**
 * Workflows to send as `memberWorkflows` for `normal_pack` onboard only.
 *
 * Includes **manually bundled** sibling operators (`source !== "extracted"`), not rows
 * persisted from `subworkflow_call` graph extraction. Orchestrator installs use
 * `installType: "orchestrator_pack"` with **no** `memberWorkflows`; the workflow
 * service copies children via BFS from the root. Listing extracted members here would ask
 * the service to onboard those UUIDs as separate marketplace roots and often yields
 * "subworkflow(s) … not found".
 */
export function memberWorkflowsForOnboard(
  rootWorkflowPublicId: string,
  employeePackMembers: unknown,
): MarketplaceOnboardMemberWorkflow[] {
  const rootNorm = rootWorkflowPublicId.trim().toLowerCase();
  if (!rootNorm || !Array.isArray(employeePackMembers)) return [];

  const seen = new Set<string>();
  const out: MarketplaceOnboardMemberWorkflow[] = [];

  for (const m of employeePackMembers) {
    if (!m || typeof m !== "object") continue;
    const row = m as {
      source?: string;
      workflowPublicId?: unknown;
      versionPublicId?: unknown;
    };
    if (row.source === "extracted") continue;
    const raw = row.workflowPublicId;
    if (typeof raw !== "string") continue;
    const id = raw.trim();
    if (!id || id.toLowerCase() === rootNorm) continue;
    const rawVersion = row.versionPublicId;
    if (typeof rawVersion !== "string" || !rawVersion.trim()) continue;
    const versionPublicId = rawVersion.trim();
    const key = id.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ workflowId: id, versionPublicId });
    if (out.length >= EMPLOYEE_PACK_MEMBER_MAX) break;
  }

  return out;
}

export function resolveMarketplaceOnboardInstallType(params: {
  listingIsEmployeePack: boolean;
  versionIsEmployeePack: boolean;
  employeePackMembers: unknown;
  sourceWorkflowSnapshot: unknown;
}): MarketplaceOnboardInstallType {
  const {
    listingIsEmployeePack,
    versionIsEmployeePack,
    employeePackMembers,
    sourceWorkflowSnapshot,
  } = params;

  const subRefs = extractSubworkflowRefsFromDefinition(sourceWorkflowSnapshot);
  const hasSubworkflowsInGraph = subRefs.length > 0;
  const hasOrchestratorFromMembers =
    membersIndicateOrchestratorWorkflow(employeePackMembers);

  if (hasSubworkflowsInGraph || hasOrchestratorFromMembers) {
    return "orchestrator_pack";
  }

  const memberCount = countEmployeePackMembersWithIds(employeePackMembers);
  const packListed = listingIsEmployeePack || versionIsEmployeePack;

  if (packListed || memberCount > 1) {
    return "normal_pack";
  }

  return "single";
}

function stripResolution(m: EnrichedEmployeePackMember): EmployeePackMember {
  const { resolutionFailed: _r, ...rest } = m;
  return rest;
}

/**
 * Merge server-enriched extraction with client round-trip and manual-only rows.
 * Every workflow-defined subworkflow (enrichedExtracted) is always persisted so
 * publishers do not need to manually add IDs they cannot see in the UI.
 * Client rows are used only to merge display names, descriptions, and marketplace links.
 */
export function mergeEmployeePackMembersForSave(params: {
  enrichedExtracted: EnrichedEmployeePackMember[];
  clientMembers: EmployeePackMember[];
}): EmployeePackMember[] {
  const { enrichedExtracted, clientMembers } = params;
  const extractedIds = new Set(
    enrichedExtracted.map((m) => m.workflowPublicId),
  );

  if (enrichedExtracted.length === 0) {
    return clientMembers
      .filter((m) => m.source === "manual" && m.workflowPublicId?.trim())
      .map((m) => ({
        workflowPublicId: m.workflowPublicId.trim(),
        versionPublicId: m.versionPublicId?.trim() || undefined,
        displayName: m.displayName?.trim() || m.workflowPublicId.trim(),
        description: m.description?.trim() || undefined,
        referencingStepIds: Array.isArray(m.referencingStepIds)
          ? m.referencingStepIds
          : [],
        source: "manual" as const,
        marketplaceSlug: m.marketplaceSlug,
        marketplaceIconAssetId: m.marketplaceIconAssetId ?? undefined,
        lastResolvedAt: m.lastResolvedAt,
      }));
  }

  const clientById = new Map(clientMembers.map((m) => [m.workflowPublicId, m]));
  const result: EmployeePackMember[] = [];

  for (const ex of enrichedExtracted) {
    const c = clientById.get(ex.workflowPublicId);
    if (c) {
      const { resolutionFailed: _rf, ...exRest } = ex;
      result.push({
        ...exRest,
        versionPublicId:
          c.versionPublicId?.trim() || ex.versionPublicId?.trim() || undefined,
        displayName: (c.displayName && c.displayName.trim()) || ex.displayName,
        description:
          (c.description && c.description.trim()) ||
          ex.description ||
          undefined,
        referencingStepIds: ex.referencingStepIds,
        marketplaceSlug: c.marketplaceSlug ?? ex.marketplaceSlug,
        marketplaceIconAssetId:
          c.marketplaceIconAssetId ?? ex.marketplaceIconAssetId,
        source: "extracted",
        lastResolvedAt: ex.lastResolvedAt,
      });
    } else {
      result.push(stripResolution(ex));
    }
  }

  for (const c of clientMembers) {
    if (c.source !== "manual") continue;
    if (extractedIds.has(c.workflowPublicId)) continue;
    if (!c.workflowPublicId.trim()) continue;
    result.push({
      workflowPublicId: c.workflowPublicId.trim(),
      versionPublicId: c.versionPublicId?.trim() || undefined,
      displayName: c.displayName?.trim() || c.workflowPublicId.trim(),
      description: c.description?.trim() || undefined,
      referencingStepIds: Array.isArray(c.referencingStepIds)
        ? c.referencingStepIds
        : [],
      source: "manual",
      marketplaceSlug: c.marketplaceSlug,
      marketplaceIconAssetId: c.marketplaceIconAssetId ?? undefined,
      lastResolvedAt: c.lastResolvedAt,
    });
  }

  return result.slice(0, EMPLOYEE_PACK_MEMBER_MAX);
}

export function packHasUnresolvedMembers(
  members: EnrichedEmployeePackMember[],
): boolean {
  return members.some((m) => m.resolutionFailed);
}

/** Asset ids stored on pack members for marketplace listing icons (deduped). */
export function collectPackIconAssetIds(members: unknown): string[] {
  if (!Array.isArray(members)) return [];
  const ids: string[] = [];
  for (const row of members) {
    if (!row || typeof row !== "object") continue;
    const id = (row as { marketplaceIconAssetId?: string | null })
      .marketplaceIconAssetId;
    if (typeof id === "string" && id.trim()) ids.push(id.trim());
  }
  return [...new Set(ids)];
}

/** Max free workflow executions allowed per marketplace listing in Try Mode. */
export const FREE_TRY_LIMIT = 3;

export function remainingFreeTries(triesUsed: number): number {
  return Math.max(0, FREE_TRY_LIMIT - triesUsed);
}

export function usedFreeTries(
  triesUsedOrRemaining: number,
  opts?: { fromRemaining?: boolean },
): number {
  if (opts?.fromRemaining) {
    return Math.max(
      0,
      Math.min(FREE_TRY_LIMIT, FREE_TRY_LIMIT - triesUsedOrRemaining),
    );
  }
  return Math.max(0, Math.min(FREE_TRY_LIMIT, triesUsedOrRemaining));
}
